import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight, faCheckDouble, faChevronRight, faArrowUpRightFromSquare, faBell, faInbox, faUser, faBuilding } from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';
import Notifications from './Notifications';
import NotificationSent from '../Admin/NotificationSent';
import NotificationDetailDrawer from '../../components/NotificationDetailDrawer';
import DateRangeFilter from '../../components/DateRangeFilter';
import NotificationTypeFilter from '../../components/NotificationTypeFilter';
import { notificationDateRange } from '../../utils/notificationDateFilter';
import { subscribeActions, refreshActions, patchActions } from '../../utils/notificationActions';
import '../../styles/notifications.css';

const labels = { TASK_APPROVAL: 'Task approvals for me', TASK: 'My tasks', LEAVE: 'Leave requests', HALF_DAY: 'Half-day and short-leave requests', WFH: 'WFH requests', EXPENSE: 'Expense approvals', ATTENDANCE: 'Attendance to update' };
const activityLabels = { TASK: 'Task activity', LEAVE: 'Leave activity', SHORT_LEAVE: 'Short leave', WFH: 'Work from home', SALARY: 'Payroll', BIRTHDAY: 'Birthdays', SYSTEM: 'System updates' };
const actionTypes = Object.entries(labels);
const activityTypes = Object.entries(activityLabels).filter(([key]) => key !== 'BIRTHDAY');
const ORDER = Object.keys(labels);
const OVERALL_STALE_MS = 30 * 1000;
const shortDate = value => (value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

export default function NotificationCenter() {
    const [params] = useSearchParams();
    const admin = JSON.parse(localStorage.getItem('user') || '{}')?.role === 'ADMIN';
    const sent = admin && params.get('tab') === 'sent';

    const [view, setView] = useState('actions');
    // Views stay mounted once opened, so switching back doesn't reload them.
    const [visited, setVisited] = useState({ actions: true });
    const [datePreset, setDatePreset] = useState('All');
    const [customDates, setCustomDates] = useState({ from: '', to: '' });
    const [typeByView, setTypeByView] = useState({ actions: 'All', overall: 'All' });
    const [page, setPage] = useState(1);
    const [store, setStore] = useState(null);
    const [filtered, setFiltered] = useState(null);
    const [overall, setOverall] = useState(null);
    const [overallRefresh, setOverallRefresh] = useState(0);
    const overallCache = useRef(new Map());
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [selected, setSelected] = useState(null);
    const [approvingId, setApprovingId] = useState(null);
    const [marking, setMarking] = useState(false);

    const { from, to } = notificationDateRange(datePreset, customDates);
    const typeFilter = typeByView[view] || 'All';
    const dated = Boolean(from || to);
    const datesKey = `${from}|${to}`;
    const overallKey = `${page}|${datesKey}|${typeFilter}`;
    const storeLoadedAt = store?.loadedAt;

    useEffect(() => subscribeActions(setStore), []);

    // With a date range the shared (unfiltered) list can't answer, so ask for
    // that range — and again whenever the shared list itself is refreshed.
    useEffect(() => {
        if (sent || view !== 'actions' || !dated) return undefined;
        let active = true;
        setFetching(true);
        api.get('/notifications/actions', { params: { from, to } })
            .then(res => { if (active) { setFiltered({ key: datesKey, rows: res.data.data || [] }); setError(''); } })
            .catch(err => { if (active) setError(err.response?.data?.message || 'Could not load notifications. Please refresh.'); })
            .finally(() => { if (active) setFetching(false); });
        return () => { active = false; };
    }, [sent, view, dated, from, to, datesKey, storeLoadedAt]);

    useEffect(() => {
        if (sent || view !== 'overall') return undefined;
        const cached = overallCache.current.get(overallKey);
        if (cached) setOverall(cached);
        if (cached && Date.now() - cached.at < OVERALL_STALE_MS) return undefined;
        let active = true;
        setFetching(true);
        api.get('/notifications/overall', { params: { page, from, to, ...(typeFilter !== 'All' ? { type: typeFilter } : {}) } })
            .then(res => {
                const entry = { key: overallKey, rows: res.data.data || [], total: res.data.total || 0, at: Date.now() };
                overallCache.current.set(overallKey, entry);
                if (active) { setOverall(entry); setError(''); }
            })
            .catch(err => { if (active) setError(err.response?.data?.message || 'Could not load notifications. Please refresh.'); })
            .finally(() => { if (active) setFetching(false); });
        return () => { active = false; };
    }, [sent, view, overallKey, page, from, to, typeFilter, overallRefresh]);

    const actionsView = view === 'actions';
    let rows = null;
    if (actionsView) rows = dated ? (filtered?.key === datesKey ? filtered.rows : null) : (storeLoadedAt ? store.rows : null);
    else if (view === 'overall') rows = overall?.key === overallKey ? overall.rows : null;
    const shownError = error || (actionsView && !dated ? store?.error : '');
    const loading = rows === null && !shownError;
    const list = actionsView && typeFilter !== 'All' ? (rows || []).filter(row => row.category === typeFilter) : (rows || []);
    const updating = fetching || (actionsView && !dated && Boolean(store?.loading));

    const switchView = key => {
        setView(key);
        setVisited(v => ({ ...v, [key]: true }));
        setError('');
        setNotice('');
    };
    const changePreset = next => { setDatePreset(next); setPage(1); };
    const changeCustomDates = next => { setCustomDates(next); setPage(1); };
    const changeType = next => { setTypeByView(current => ({ ...current, [view]: next })); setPage(1); };
    const refresh = () => {
        setNotice('');
        if (actionsView) refreshActions({ force: true });
        else { overallCache.current.delete(overallKey); setOverallRefresh(n => n + 1); }
    };

    const markRowsRead = keys => {
        const read = r => (keys === 'all' || keys.includes(r._id) ? { ...r, isRead: true } : r);
        patchActions(s => ({ rows: s.rows.map(read), unread: keys === 'all' ? 0 : s.rows.filter(r => !read(r).isRead).length }));
        setFiltered(f => f && { ...f, rows: f.rows.map(read) });
    };

    // Looking at an action counts as reading it; it stays listed until done.
    const markViewed = row => {
        if (actionsView && !row.isRead) {
            markRowsRead([row._id]);
            api.put('/notifications/views/read', { view: 'actions', key: row._id }).catch(() => {});
        }
    };

    const openRow = row => {
        setError('');
        setSelected(row);
        markViewed(row);
    };

    const approve = async row => {
        if (!row.approval || approvingId) return;
        setApprovingId(row._id); setError(''); setNotice('');
        try {
            // The endpoint re-checks the request is still pending, so there's
            // no need to reload the whole list first.
            await api.put(row.approval.endpoint, row.approval.body);
            patchActions(s => ({
                rows: s.rows.filter(r => r._id !== row._id),
                count: Math.max(0, s.count - 1),
                unread: row.isRead ? s.unread : Math.max(0, s.unread - 1)
            }));
            setFiltered(f => f && { ...f, rows: f.rows.filter(r => r._id !== row._id) });
            setSelected(null);
            setNotice('Approved.');
        } catch (err) {
            setError(err.response?.data?.message || 'Approval failed. Please try again.');
        } finally {
            setApprovingId(null);
            refreshActions({ force: true });
        }
    };

    const markAllRead = async () => {
        if (marking) return;
        setMarking(true); setError(''); setNotice('');
        try {
            await api.put('/notifications/views/mark-all-read', { view, from, to, ...(typeFilter !== 'All' ? { type: typeFilter } : {}) });
            if (actionsView) {
                if (dated || typeFilter !== 'All') { markRowsRead(list.map(r => r._id)); refreshActions({ force: true }); }
                else markRowsRead('all');
                setNotice('Marked as read. Items stay here until the work is done.');
            } else {
                overallCache.current.clear();
                setOverall(o => o && { ...o, rows: o.rows.map(r => ({ ...r, isRead: true })) });
                setNotice('Marked as read.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Could not mark notifications as read.');
        } finally { setMarking(false); }
    };

    const groups = list.reduce((acc, row) => {
        const key = actionsView ? row.category : row.type;
        (acc[key] ||= []).push(row);
        return acc;
    }, {});
    const groupKeys = Object.keys(groups).sort((a, b) => (actionsView ? ORDER.indexOf(a) - ORDER.indexOf(b) : 0));
    const total = overall?.key === overallKey ? overall.total : 0;

    return <div className="attendance-container nc-center">
        <header className="nc-heading">
            <div className="nc-heading-main">
                <span className="nc-heading-icon"><FontAwesomeIcon icon={faBell} /></span>
                <div>
                    <h1 className="page-title header-no-margin">Notification center</h1>
                    <p>Stay on top of what needs your attention and what has happened.</p>
                </div>
            </div>
            <div className="nc-heading-stats" aria-label="Notification overview">
                <span><strong>{store?.count ?? '—'}</strong> open actions</span>
                <span><strong>{store?.unread ?? '—'}</strong> unread</span>
            </div>
        </header>
        <nav aria-label="Notification mailbox" className="nc-tabs">
            <Link to="/notifications" aria-current={!sent ? 'page' : undefined}>Received</Link>
            {admin && <Link to="/notifications?tab=sent" aria-current={sent ? 'page' : undefined}>Sent</Link>}
        </nav>
        {sent ? <NotificationSent /> : <section aria-label="Received notifications" className="nc-layout">
            <aside className="nc-side">
                <p className="nc-side-label">Received</p>
                <nav aria-label="Received notification views" className="nc-view-nav">
                    {[
                        ['actions', 'Action required', 'Work waiting on you', faInbox],
                        ['personal', 'Directly for me', 'Your own updates', faUser],
                        ...(admin ? [['overall', 'Company activity', 'Activity across teams', faBuilding]] : [])
                    ].map(([key, label, description, icon]) =>
                        <button key={key} type="button" aria-pressed={view === key} onClick={() => switchView(key)}>
                            <span className="nc-view-icon"><FontAwesomeIcon icon={icon} /></span>
                            <span className="nc-view-copy"><strong>{label}</strong><small>{description}</small></span>
                            {key === 'actions' && store?.count > 0 && <span className="nc-view-count" aria-label={`${store.count} open actions`}>{store.count}</span>}
                        </button>)}
                </nav>
                <p className="nc-side-note">{view === 'actions' ? 'Completed actions leave this inbox automatically.' : view === 'overall' ? 'Company activity is for reference and does not affect your unread badge.' : 'Your personal updates and decisions appear here.'}</p>
            </aside>
            <div className="nc-workspace">

            {visited.personal && <div hidden={view !== 'personal'}><Notifications embedded /></div>}

            {view !== 'personal' && <>
                <div className="nc-section-heading nc-workspace-heading">
                    <div><span className="nc-eyebrow">{actionsView ? 'YOUR INBOX' : 'COMPANY TIMELINE'}</span>
                        <h2>{actionsView ? 'Action required' : 'Company activity'}</h2>
                        <p>{actionsView ? 'Responsibilities grouped by category. Items clear when the work is completed.' : 'History across the company, including requests assigned to other people.'}</p>
                    </div>
                    <span className="nc-heading-count">{!loading && !shownError ? (actionsView ? list.length : total) : '—'}</span>
                </div>
                <div className="nc-filter-panel">
                    <div className="nc-filter-label">Show activity from</div>
                    <DateRangeFilter value={datePreset} onChange={changePreset} custom={customDates} onCustomChange={changeCustomDates} inline />
                </div>
                <NotificationTypeFilter options={actionsView ? actionTypes : activityTypes} value={typeFilter} onChange={changeType} />
                <div className="nc-toolbar">
                    <span className="nc-result-summary">{!loading && !shownError ? `${actionsView ? list.length : total} ${actionsView ? 'open action' : 'activity item'}${(actionsView ? list.length : total) === 1 ? '' : 's'}` : 'Notifications'}</span>
                    <div className="nc-toolbar-actions">
                        {updating && !loading && <span className="nc-updating" role="status">Updating…</span>}
                        <button type="button" className="nc-btn ghost" disabled={updating} onClick={refresh}>
                            <FontAwesomeIcon icon={faRotateRight} spin={updating} /> Refresh
                        </button>
                        <button type="button" className="nc-btn ghost" disabled={marking || !list.some(row => !row.isRead)} onClick={markAllRead}>
                            <FontAwesomeIcon icon={faCheckDouble} /> {marking ? 'Marking…' : 'Mark all as read'}
                        </button>
                    </div>
                </div>

                {notice && <p role="status" className="nc-notice">{notice}</p>}
                {shownError && <p role="alert" className="nc-error">{shownError}</p>}

                {loading && <ul className="nc-skeleton" aria-busy="true" aria-label="Loading notifications"><li /><li /><li /></ul>}

                {!loading && !list.length && !shownError && <div className="nc-empty">
                    <h3>{typeFilter !== 'All' || dated ? 'Nothing matches these filters' : actionsView ? 'You’re all caught up' : 'No activity yet'}</h3>
                    <p>{typeFilter !== 'All' || dated ? 'Try another type or a wider date range.' : actionsView ? 'New responsibilities will appear here.' : 'Company notifications will appear here as they arrive.'}</p>
                    <button type="button" className="nc-btn ghost" onClick={() => switchView('personal')}>View personal updates</button>
                </div>}

                {groupKeys.map(category => <details key={category} className="nc-group" open>
                    <summary>{(actionsView ? labels : activityLabels)[category] || category}<span className="nc-count">{groups[category].length}</span></summary>
                    <ul>{groups[category].map(row => <li key={row._id} className={row.isRead ? 'nc-action-row' : 'nc-action-row nc-unread'}>
                        <div className="nc-action-copy">
                            <span className="nc-row-title">{row.title}</span>
                            <span className="nc-row-meta">
                                {actionsView ? row.summary : `For ${row.recipient?.name || 'Former user'}`}
                                {row.createdAt && <>{(actionsView ? row.summary : true) && ' · '}{shortDate(row.createdAt)}</>}
                            </span>
                            {!actionsView && row.message && <span className="nc-row-message">{row.message}</span>}
                        </div>
                        <div className="nc-row-buttons">
                            {actionsView && row.approval && <button type="button" className="nc-btn approve" disabled={Boolean(approvingId)} onClick={() => approve(row)}>
                                {approvingId === row._id ? 'Approving…' : 'Approve'}
                            </button>}
                            {row.link && <Link className="nc-btn ghost" to={row.link} aria-label={`Open: ${row.title}`}
                                onClick={() => markViewed(row)}>
                                Open <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            </Link>}
                            <button type="button" className="nc-btn ghost" onClick={() => openRow(row)} aria-label={`Details: ${row.title}`}>
                                Details <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    </li>)}</ul>
                </details>)}

                {view === 'overall' && total > 25 && <div className="nc-pagination">
                    <button type="button" className="nc-btn ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                    <span>Page {page} of {Math.ceil(total / 25)}</span>
                    <button type="button" className="nc-btn ghost" disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>}
            </>}
            </div>
        </section>}
        {selected && <NotificationDetailDrawer row={selected} onClose={() => setSelected(null)} onApprove={approve} busy={approvingId === selected._id} error={error} />}
    </div>;
}
